import {
	Atom,
	AtomOptions,
	ComputedAtom,
	ComputedAtomOptions,
	Reaction,
	ReactionOptions,
	ReactiveLayer,
} from "fluidstate";
import { batch, computed, signal, untracked } from "@preact/signals-core";
import { Effect } from "./relative-layer-effect";

/**
 * Returns a `ReactiveLayer` implementation backed by the `@preact/signals-core`
 * library. This function provides the bridge between `fluidstate`'s abstract
 * reactivity API and `@preact/signals-core` reactivity implementation.
 */
export const getReactiveLayer = (): ReactiveLayer => ({
	createAtom: (name: string, options?: AtomOptions): Atom => {
		let isObserved = false;
		let i = 0;

		const preactSignal = signal(i, {
			watched: () => {
				options?.onBecomeObservedListener?.();
				isObserved = true;
			},
			unwatched: () => {
				options?.onBecomeUnobservedListener?.();
				isObserved = false;
			},
		});

		const result = {
			name,
			reportObserved() {
				preactSignal.value;
				return isObserved;
			},
			reportChanged() {
				preactSignal.value = ++i;
			},
		};

		return result;
	},

	createComputedAtom: <T>(
		name: string,
		calculate: () => T,
		options?: ComputedAtomOptions
	): ComputedAtom<T> => {
		let isInitialValue = true;
		let lastValue: null | T = null;
		let i = 0;

		const preactComputed = computed(
			() => {
				const newValue = calculate();

				if (isInitialValue) {
					isInitialValue = false;

					lastValue = newValue;
					return i;
				}

				if (!(options?.equals ?? Object.is)(lastValue, newValue)) {
					lastValue = newValue;
					return ++i;
				}

				lastValue = newValue;
				return i;
			},
			{
				watched: options?.onBecomeObservedListener,
				unwatched: options?.onBecomeUnobservedListener,
			}
		);

		const getValue = Object.getOwnPropertyDescriptor(
			Object.getPrototypeOf(preactComputed),
			"value"
		)?.get!;

		Object.defineProperty(preactComputed, "value", {
			get: () => {
				getValue.call(preactComputed);
				return lastValue;
			},
		});

		const result = {
			name,
			get() {
				return preactComputed.value as T;
			},
		};

		return result;
	},

	createReaction: (fn: () => void, options?: ReactionOptions): Reaction => {
		const effect = new Effect(fn, options);

		const result = {
			stop: () => {
				effect.stop();
			},
		};

		return result;
	},

	untrack: <T>(action: () => T): T => {
		return untracked(action);
	},

	runTransaction: <T>(transaction: () => T): T => {
		return batch(transaction);
	},

	runAction: <T>(action: () => T): T => {
		return batch(action);
	},
});
